import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Nav } from "@/components/festival/Nav";
import { Footer } from "@/components/festival/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock, Film, Tag, Timer, Users } from "lucide-react";
import { getFullProgram, type PublicFilm } from "@/lib/films.functions";
import { getFilmActivities, type PublicActivity } from "@/lib/film-activities.functions";
import { ActivitiesBadge, ActivitiesStrip } from "@/components/festival/FilmActivities";
import { SITE_URL } from "@/i18n/translations";
import { TrailerController, TrailerSection } from "@/components/festival/trailer/TrailerSection";
import { parseYouTubeId, youtubeEmbedUrl } from "@/lib/youtube";
import { RichText } from "@/components/admin/RichText";
import { resolveBadgeStyle } from "@/lib/badges";
import { useCustomBadges } from "@/hooks/use-custom-badges";

export const Route = createFileRoute("/programma")({
  head: () => ({
    meta: [
      { title: "Programma 2026 — Openluchtfilmfestival Apeldoorn" },
      {
        name: "description",
        content:
          "Ontdek het volledige filmprogramma van het Openluchtfilmfestival Apeldoorn: negen zomeravonden, negen films onder de sterren in Stadspark Berg & Bos.",
      },
      { property: "og:title", content: "Programma 2026 — Openluchtfilmfestival Apeldoorn" },
      {
        property: "og:description",
        content: "Negen avonden, negen films. Bekijk het volledige programma van 14 t/m 22 augustus 2026.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/programma` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/programma` }],
  }),
  component: ProgrammaPage,
});

const WEEKDAYS_NL = ["zondag", "maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag"];
const WEEKDAYS_SHORT_NL = ["zo", "ma", "di", "wo", "do", "vr", "za"];
const MONTHS_NL = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];

function parseDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}
function slugify(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
function formatFull(iso: string) {
  const d = parseDate(iso);
  return `${WEEKDAYS_NL[d.getDay()]} ${d.getDate()} ${MONTHS_NL[d.getMonth()]}`;
}
function chipParts(iso: string) {
  const d = parseDate(iso);
  return { weekday: WEEKDAYS_SHORT_NL[d.getDay()], day: d.getDate() };
}

function ProgrammaPage() {
  return (
    <I18nProvider lang="nl">
      <div className="min-h-screen bg-background">
        <Nav />
        <main className="pt-24 md:pt-32">
          <ProgrammaContent />
        </main>
        <Footer />
      </div>
    </I18nProvider>
  );
}

function ProgrammaContent() {
  const [films, setFilms] = useState<PublicFilm[] | null>(null);
  const [activities, setActivities] = useState<Record<string, PublicActivity[]>>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const customBadges = useCustomBadges();

  useEffect(() => {
    getFullProgram()
      .then(setFilms)
      .catch(() => setFilms([]));
    getFilmActivities()
      .then(setActivities)
      .catch(() => setActivities({}));
  }, []);

  // Deep-link: scroll to #film-<id> and highlight the card once films are loaded.
  useEffect(() => {
    if (!films || films.length === 0) return;
    const applyFromHash = () => {
      const raw = window.location.hash.replace(/^#/, "");
      if (!raw.startsWith("film-")) return;
      const id = raw.slice("film-".length);
      if (!films.some((f) => f.id === id)) return;
      const el = document.getElementById(`film-${id}`);
      if (!el) return;
      // Defer scroll to allow layout/images to settle.
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      setHighlightedId(id);
      window.setTimeout(() => {
        setHighlightedId((current) => (current === id ? null : current));
      }, 2600);
    };
    applyFromHash();
    window.addEventListener("hashchange", applyFromHash);
    return () => window.removeEventListener("hashchange", applyFromHash);
  }, [films]);

  // Scroll-spy: highlight active day chip while scrolling
  useEffect(() => {
    if (!films || films.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveId(visible.target.id.replace(/^film-/, ""));
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    films.forEach((f) => {
      const el = document.getElementById(`film-${f.id}`);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [films]);

  const jsonLd = useMemo(() => {
    if (!films || films.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Openluchtfilmfestival Apeldoorn — Programma 2026",
      itemListElement: films.map((f, i) => {
        const trailerId = parseYouTubeId(f.trailer_url ?? null);
        return ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "ScreeningEvent",
          name: f.title,
          startDate: f.start_time ? `${f.play_date}T${f.start_time}` : f.play_date,
          description: f.teaser ?? f.description ?? undefined,
          image: f.poster_url ?? undefined,
          location: {
            "@type": "Place",
            name: "Stadspark Berg & Bos",
            address: {
              "@type": "PostalAddress",
              streetAddress: "J.C. Wilslaan",
              addressLocality: "Apeldoorn",
              addressCountry: "NL",
            },
          },
          ...(trailerId
            ? {
                trailer: {
                  "@type": "VideoObject",
                  name: `Trailer — ${f.title}`,
                  embedUrl: youtubeEmbedUrl(trailerId),
                  thumbnailUrl: `https://i.ytimg.com/vi/${trailerId}/hqdefault.jpg`,
                  uploadDate: f.play_date,
                  description: f.teaser ?? f.description ?? `Trailer voor ${f.title}`,
                },
              }
            : {}),
        },
      });
      }),
    };
  }, [films]);

  if (!films) {
    return (
      <section className="mx-auto max-w-3xl px-5 py-24 text-center md:px-8">
        <p className="font-mono-cinema text-[10px] text-gold">— Programma laden…</p>
      </section>
    );
  }

  if (films.length === 0) {
    return (
      <section className="mx-auto max-w-3xl px-5 py-24 text-center md:px-8">
        <p className="font-mono-cinema text-[10px] text-gold">— Programma · editie 2026</p>
        <h1 className="mt-5 font-display text-4xl font-medium leading-[1.05] md:text-6xl">Binnenkort onthuld</h1>
        <p className="mt-6 text-foreground/70 md:text-lg">
          Het volledige programma wordt binnenkort bekendgemaakt. Meld je aan voor updates en ontvang het als eerste.
        </p>
        <div className="mt-8 flex justify-center">
          <Button asChild variant="hero">
            <a href="/#updates">
              Ontvang het programma <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
        </div>
      </section>
    );
  }

  return (
    <>
      {jsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />}

      <section className="relative py-8 md:py-14">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="font-mono-cinema text-[10px] text-gold">— Programma · editie 2026</p>
            <h1 className="mt-5 font-display text-4xl font-medium leading-[1.05] md:text-6xl lg:text-7xl">
              Negen avonden,
              <span className="italic text-gold"> negen films</span>.
            </h1>
            <p className="mt-6 text-foreground/70 md:text-lg">
              Van 14 tot en met 22 augustus 2026 in Stadspark Berg & Bos. Ontdek elke avond hieronder, of spring direct
              naar de film die je aanspreekt.
            </p>
          </div>

          {/* Sticky day nav */}
          <div className="sticky top-16 z-20 -mx-5 mt-12 border-y border-border/60 bg-background/85 px-5 py-3 backdrop-blur-xl md:top-20 md:mx-0 md:rounded-full md:border md:px-4">
            <div className="flex gap-2 overflow-x-auto md:justify-center md:flex-wrap">
              {films.map((f) => {
                const c = chipParts(f.play_date);
                const isActive = activeId === f.id;
                return (
                  <a
                    key={f.id}
                    href={`#film-${f.id}`}
                    className={`flex min-w-[56px] flex-col items-center rounded-lg px-3 py-2 transition-all duration-300 ${
                      isActive ? "bg-gold/15 text-gold ring-1 ring-gold/50" : "text-foreground/70 hover:text-gold"
                    }`}
                  >
                    <span className="font-mono-cinema text-[9px] uppercase tracking-wider">{c.weekday}</span>
                    <span className="font-display text-lg leading-none">{c.day}</span>
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <TrailerController>
          <div className="flex flex-col gap-16 md:gap-24">
            {films.map((f, i) => (
              <article
                key={f.id}
                id={`film-${f.id}`}
                data-highlight={highlightedId === f.id ? "true" : undefined}
                className="scroll-mt-40 grid gap-8 rounded-3xl md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:gap-12"
              >
                <div className="film-grain relative aspect-[2/3] w-full max-w-sm overflow-hidden rounded-2xl border border-border bg-gradient-night md:max-w-none">
                  {f.poster_url ? (
                    <img
                      src={f.poster_url}
                      alt={f.title}
                      className="h-full w-full object-cover"
                      loading={i < 2 ? "eager" : "lazy"}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Film className="h-16 w-16 text-gold/40" />
                    </div>
                  )}
                  <div className="pointer-events-none absolute inset-0 bg-projector opacity-30 mix-blend-overlay" />
                </div>

                <div className="flex flex-col justify-center">
                  <p className="font-mono-cinema text-[10px] uppercase tracking-[0.25em] text-gold">
                    Avond {f.festival_day} · {formatFull(f.play_date)}
                    {f.start_time ? ` · start film ${f.start_time}` : ""}
                  </p>
                  <h2 className="mt-3 font-display text-3xl font-medium leading-[1.05] md:text-5xl">{f.title}</h2>

                  {(f.genre || f.age_rating || f.duration_minutes || f.start_time) && (
                    <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground/75">
                      {f.genre && (
                        <span className="flex items-center gap-1.5">
                          <Tag className="h-3.5 w-3.5 text-gold" /> {f.genre}
                        </span>
                      )}
                      {f.age_rating && (
                        <span className="flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5 text-gold" /> Kijkwijzer {f.age_rating}
                        </span>
                      )}
                      {f.duration_minutes ? (
                        <span className="flex items-center gap-1.5">
                          <Timer className="h-3.5 w-3.5 text-gold" /> {f.duration_minutes} min
                        </span>
                      ) : null}
                      {f.start_time && (
                        <span className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-gold" /> {f.start_time}
                        </span>
                      )}
                    </div>
                  )}

                  {f.badges.length > 0 && (
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      {f.show_activities_badge && <ActivitiesBadge label={f.activities_badge_label} />}
                      {f.badges.map((b) => {
                        const s = resolveBadgeStyle(b, customBadges);
                        return (
                          <span
                            key={b}
                            className={`rounded-full px-3 py-1.5 font-mono-cinema text-[10px] font-semibold uppercase tracking-wider ${s.className}`}
                            style={s.style}
                          >
                            {b}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {f.badges.length === 0 && f.show_activities_badge && (
                    <div className="mt-4">
                      <ActivitiesBadge label={f.activities_badge_label} />
                    </div>
                  )}

                  {f.teaser && <p className="mt-6 text-lg italic text-foreground/85">&ldquo;{f.teaser}&rdquo;</p>}
                  {f.description && (
                    <RichText html={f.description} className="mt-4 text-foreground/75 md:text-lg" />
                  )}

                  {(activities[f.id]?.length ?? 0) > 0 && (
                    <>
                      <ActivitiesStrip items={activities[f.id]!} />
                      <Link
                        to="/films/$slug"
                        params={{ slug: slugify(f.title) }}
                        className="mt-3 inline-flex items-center gap-1.5 font-mono-cinema text-[10px] uppercase tracking-wider text-gold hover:underline"
                      >
                        Bekijk het voorprogramma <ArrowRight className="h-3 w-3" />
                      </Link>
                    </>
                  )}

                  {(parseYouTubeId(f.trailer_url ?? null) || (f.cta_url && /^https?:\/\//i.test(f.cta_url))) && (
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                      <TrailerSection filmId={f.id} filmTitle={f.title} trailerUrl={f.trailer_url ?? null} />
                      {f.cta_url && /^https?:\/\//i.test(f.cta_url) && (
                        <Button asChild variant="hero" size="lg">
                          <a href={f.cta_url} target="_blank" rel="noreferrer noopener">
                            {f.cta_label ?? "Meer info"} <ArrowRight className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
          </TrailerController>

          <p className="mt-16 text-center font-mono-cinema text-[10px] text-foreground/60">
            ● Programma onder voorbehoud · Wijzigingen mogelijk
          </p>
        </div>
      </section>
    </>
  );
}
